import _ from "lodash";
import { convertToDotNotation } from "@Features/viewer-modals/components/Modal/SearchSet/searchSetHelper";
import type { ModelPropertiesData } from "@WebTypes/modelProperties";
import { extractPropertyPartialName } from "@Features/viewer-modals/components/Modal/SearchSet1/searchSetHelper";
import { convertDate } from "@Services/luxonHelper";

export const escapeRegexCharacters = (value: any) => {
  if (value && typeof value === "string") {
    return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  }
  return value;
};

export const getNestedValue = (obj: any, field: string) => {
  if (!field) return undefined;
  if (_.get(obj, field) && typeof _.get(obj, field) === "object") {
    return field.split(".").reduce((acc: any, part: any) => acc?.[part], obj).displayText;
  }
  return field.split(".").reduce((acc: any, part: any) => acc?.[part], obj);
};

export const buildPredicate = (q: any): ((item: any) => boolean) => {
  if (!q || !Object.keys(q).length) {
    return () => true;
  }
  if (Object.hasOwn(q, "condition")) {
    const preds = q.rules.map((rule: any) => buildPredicate(rule));
    if (q.condition === "and") {
      return (item: any) => preds.every((p: any) => p(item));
    }
    if (q.condition === "or") {
      return (item: any) => preds.some((p: any) => p(item));
    }
    return () => true;
  }
  const testFunction: any = getFieldOperatorValue({
    operator: q.operator,
    value: q.value,
  });
  return (item: any) => testFunction(getNestedValue(item, q.field));
};

const normalizeToList = (itemValue: any) => {
  if (Array.isArray(itemValue)) return itemValue;
  if (itemValue && typeof itemValue === "object") return [itemValue.displayText];
  return itemValue != null ? [itemValue] : [];
};

export const getFieldOperatorValue = ({ operator, value }: any) => {
  if (Array.isArray(value) && value.length > 0) {
    value = value[0];
  }
  switch (operator) {
    case "contains":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => new RegExp(`.*${escapeRegexCharacters(value)}.*`, "i").test(String(val)));
      };
    case "equal":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => val == value);
      };
    case "notequal":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => val !== value);
      };
    case "startswith":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => new RegExp(`^${escapeRegexCharacters(value)}`, "i").test(val));
      };
    case "endswith":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.some((val) => new RegExp(`${escapeRegexCharacters(value)}$`, "i").test(val));
      };
    case "doesnotstartwith":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.every((val) => !new RegExp(`^${escapeRegexCharacters(value)}`, "i").test(val));
      };
    case "doesnotendwith":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.every((val) => !new RegExp(`${escapeRegexCharacters(value)}$`, "i").test(val));
      };
    case "doesnotcontain":
      return (itemValue: any) => {
        let listValues = normalizeToList(itemValue);
        return listValues.every((val) => !new RegExp(`.*${escapeRegexCharacters(value)}.*`, "i").test(val));
      };
    case "isnotempty":
      return (itemValue: any) => itemValue !== undefined && itemValue !== "" && itemValue !== null;
    case "isempty":
      return (itemValue: any) => itemValue === undefined || itemValue === "" || itemValue === null;
    case "greaterthan":
      return (itemValue: any) => {
        return itemValue > value;
      };
    case "greaterthanorequal":
      return (itemValue: any) => {
        return itemValue >= value;
      };
    case "lessthan":
      return (itemValue: any) => {
        return itemValue < value;
      };
    case "lessthanorequal":
      return (itemValue: any) => {
        return itemValue <= value;
      };
  }
};

// Operators whose meaning is negative: a slash variant counts as "no match" only when ALL forms fail.
const NEGATIVE_OPERATORS = new Set(["notequal", "doesnotcontain", "doesnotstartwith", "doesnotendwith"]);

/**
 * Slash-equivalent forms of a search value so that searching "x" also matches "/x" (and "/x" matches "x").
 * Mirrors buildWildcardPatterns in packages/server/fileOperations/fileSearchHelper.ts.
 */
export const buildSlashEquivalentValues = (value: any): any[] => {
  if (Array.isArray(value) && value.length > 0) value = value[0];
  if (typeof value !== "string") return [value];
  const stripped = value.replace(/^\/+/, "");
  const variants = new Set<string>([value]);
  if (stripped) {
    variants.add(stripped);
    variants.add(`/${stripped}`);
  }
  return [...variants];
};

/** Like getFieldOperatorValue, but a leading slash is optional so "x" and "/x" match each other. */
export const getSlashTolerantOperatorValue = ({ operator, value }: any) => {
  const testFns = buildSlashEquivalentValues(value)
    .map((variant) => getFieldOperatorValue({ operator, value: variant }))
    .filter(Boolean);
  if (testFns.length <= 1) return testFns[0];
  const negate = NEGATIVE_OPERATORS.has(operator);
  return (itemValue: any) =>
    negate ? testFns.every((fn: any) => fn(itemValue)) : testFns.some((fn: any) => fn(itemValue));
};

const evalChild = (child: any, item: any, allColumns: Set<string>): boolean | null => {
  if (child.type === "group") {
    const groupResult = applyFilter([item], child);
    if (groupResult.allColumns.size === 0) return null;
    groupResult.allColumns.forEach((col: any) => allColumns.add(col));
    return groupResult.filteredData.length > 0;
  }
  let { field, operator, value } = child.properties;
  // An incomplete rule (no field selected yet) carries field === null. Such a
  // rule never matches, and its null field must not be collected as a grid
  // column or it crashes column-building in extractPropertyPartialName.
  if (field) allColumns.add(field);
  const resolvedValue = Array.isArray(value) ? value[0] : value;
  const needsValue = !["isempty", "isnotempty"].includes(operator);
  if (!field || !operator || (needsValue && (resolvedValue == null || resolvedValue === ""))) return false;
  const testFunction = getFieldOperatorValue({ operator, value });
  return testFunction ? testFunction(_.get(item, field)) : null;
};

export const applyFilter = <T>(
  data: T[],
  query: Record<string, unknown> | null | undefined,
): { filteredData: T[]; allColumns: Set<string> } => {
  // The query is a dynamic filter tree (react-awesome-query-builder shape).
  const q = query as { children1?: unknown[]; properties?: { conjunction?: string; not?: boolean } } | null | undefined;
  if (!q?.children1 || q.children1.length === 0) {
    return { filteredData: data, allColumns: new Set<string>() };
  }
  const children1 = q.children1;
  const conjunction = q.properties?.conjunction || "AND";
  const not = !!q.properties?.not;
  const allColumns = new Set<string>();
  const filteredData = data.filter((item) => {
    const childResults = children1
      .map((child) => evalChild(child, item, allColumns))
      .filter((result) => result !== null && result !== undefined);
    let result = childResults.every((r) => r === true);
    if (conjunction === "OR") result = childResults.includes(true);
    return not ? !result : result;
  });
  return { filteredData, allColumns };
};

export const getQueryResult = (
  modelPropertiesData: ModelPropertiesData,
  query: Record<string, unknown> | null | undefined,
) => {
  const flatModelProperties = Object.values(modelPropertiesData).flatMap((obj) => Object.values(obj));
  const { filteredData } = applyFilter(flatModelProperties, query);

  return filteredData.map((data) => ({
    id: `${data.fileVersionId}_${data.nodeId}`,
    ...convertToDotNotation(data),
  }));
};

export const getFieldValue = (
  field: string,
  raw: any,
  questionMapper?: Record<string, { question?: string; type?: string }>,
): { label: string; value: string } => {
  let label = field;
  let fieldType: string | undefined;

  if (field.startsWith("customProperties.")) {
    const id = extractPropertyPartialName(field);
    const mapped = questionMapper?.[id];
    label = mapped?.question ?? id;
    fieldType = mapped?.type;
  } else if (field.startsWith("properties.") || field.startsWith("typeProperties.")) {
    label = extractPropertyPartialName(field);
  }

  let value: string;
  if (raw === undefined || raw === null || raw === "") {
    value = "";
  } else if (fieldType === "date-time") {
    value = convertDate(Number(raw), "dateForTooltip");
  } else if (Array.isArray(raw)) {
    value = raw.join(", ");
  } else if (typeof raw === "object") {
    value = raw.displayText ?? JSON.stringify(raw);
  } else {
    value = String(raw);
  }

  return { label, value };
};
